/**
 * React hook providing live Excel context state.
 *
 * On mount:
 *   1. Reads the current selection context
 *   2. Registers selection-change and sheet-activation event listeners
 *   3. Updates the ribbon button states
 *
 * On each selection change (debounced 300 ms):
 *   1. Re-reads context
 *   2. Updates ribbon
 *   3. Returns the new state to the component tree
 *
 * On unmount:
 *   - Clears debounce timer (event handlers are cleaned up by Office.js
 *     when the taskpane page is destroyed)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  readExcelContextState,
  EMPTY_CONTEXT,
  type ExcelContextState,
} from '../office/excel-context';
import { updateRibbonByContext } from '../office/ribbon';

/* global Excel, Office */

const DEBOUNCE_MS = 300;

export function useExcelContext(): ExcelContextState {
  const [state, setState] = useState<ExcelContextState>(EMPTY_CONTEXT);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const ctx = await readExcelContextState();
      setState(ctx);
      await updateRibbonByContext(ctx);
    } catch {
      // Silent — keep last known state
    }
  }, []);

  const debouncedRefresh = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void refresh(), DEBOUNCE_MS);
  }, [refresh]);

  useEffect(() => {
    // Initial context read + ribbon update
    void refresh();

    // Register Excel event listeners for live updates
    const canListenEvents =
      typeof Excel !== 'undefined' &&
      typeof Office !== 'undefined' &&
      Office.context?.requirements?.isSetSupported('ExcelApi', '1.9');

    if (canListenEvents) {
      Excel.run(async (ctx) => {
        // Collection-level event: fires for any worksheet (ExcelApi 1.9)
        ctx.workbook.worksheets.onSelectionChanged.add(async () => {
          debouncedRefresh();
        });

        // Sheet activation event (ExcelApi 1.7)
        if (Office.context.requirements.isSetSupported('ExcelApi', '1.7')) {
          ctx.workbook.worksheets.onActivated.add(async () => {
            debouncedRefresh();
          });
        }

        await ctx.sync();
      }).catch((err: unknown) => {
        console.warn('[useExcelContext] Event registration failed:', err);
      });
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [refresh, debouncedRefresh]);

  return state;
}
